import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode
} from 'react';
import { api, ApiError, CONFIRMAR_EMAIL, DOIS_FATORES_DESAFIO, DesafioDoisFatores } from '../api';
import { setShardHeader } from '../api/http';
import { useHttpApi } from '../api/mode';
import type { Account, CharacterSummary, CredencialGoogle, Credentials, Shard } from '../api';
import { aoGravarIdioma, ehIdioma, idiomaAtual, preferenciaGuardada, t, trocarIdioma } from '../i18n/idioma';
import { aoEncerrarAcesso } from './acessoEncerrado';

/**
 * O fluxo de entrada é linear e cada passo depende do anterior, então ele é
 * uma máquina de estados, não uma rota. Não dá para "digitar a URL" de uma
 * tela de personagem sem shard escolhido — e é exatamente assim que deve ser.
 */
export type Stage = 'login' | 'shard' | 'character' | 'create' | 'world';

interface SessionValue {
  readonly stage: Stage;
  readonly account: Account | null;
  readonly shard: Shard | null;
  readonly character: CharacterSummary | null;
  readonly busy: boolean;
  readonly verificando: boolean;
  readonly error: string | null;
  readonly errorCode: string | null;
  /** O e-mail que espera o código (techspec-confirmacao-email). Enquanto houver, a entrada mostra o código. */
  readonly confirmacao: string | null;
  /** O desafio do 2FA de uma conta da equipe (techspec-console-2fa-e-cloudflare-access §03.4). Enquanto houver, a entrada pede o código do app. */
  readonly doisFatores: string | null;

  signIn(credentials: Credentials): Promise<void>;
  signUp(credentials: Credentials): Promise<void>;
  signInGoogle(body: CredencialGoogle): Promise<void>;
  confirmarEmail(codigo: string, serial?: string): Promise<void>;
  reenviarCodigo(): Promise<void>;
  /** Abre a confirmação para um e-mail (o link do e-mail traz o e-mail e o código). */
  iniciarConfirmacao(email: string): void;
  cancelarConfirmacao(): void;
  /** O código do app autenticador (ou um código de recuperação) depois da senha certa da equipe. */
  confirmarDoisFatores(codigo: { codigo?: string; recuperacao?: string }): Promise<void>;
  cancelarDoisFatores(): void;
  /** "Esqueci a senha" (techspec-esqueci-senha): troca a senha com o código do e-mail e entra. */
  redefinirSenha(email: string, codigo: string, senha: string, serial?: string): Promise<void>;
  chooseShard(shard: Shard): void;
  enterWorld(character: CharacterSummary): void;
  marcarTutorialFeito(): void;
  startCreate(): void;
  cancelCreate(): void;
  finishCreate(): void;
  back(): void;
  signOut(): Promise<void>;
  clearError(): void;
}

const SessionContext = createContext<SessionValue | null>(null);

function readable(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return t('comum.algoDeuErrado');
}

function erroDeBoot(err: unknown): string | null {
  if (!(err instanceof ApiError)) return t('auth.unavailable');
  if (err.code === 'acesso.semAcesso') return t('acesso.semAcesso');
  if (err.status === 401) return null;
  if (err.status === 0 || err.status >= 500) return t('auth.unavailable');
  return null;
}

const useHttp = useHttpApi;

/** A escolha deste navegador manda. Sem ela, vale o idioma gravado na conta. */
function alinharIdioma(daConta: string | null | undefined, logado: boolean) {
  const local = preferenciaGuardada();
  if (local) {
    if (logado && daConta !== local) void api.gravarIdioma(local).catch(() => {});
    if (idiomaAtual() !== local) void trocarIdioma(local);
    return;
  }
  if (ehIdioma(daConta) && idiomaAtual() !== daConta) void trocarIdioma(daConta);
}

/** Depois de entrar pelo `#login` (ou pelo antigo `/entrar`), recarregar a página não deve reabrir o login. */
function sairDaRotaDeEntrada(): void {
  if (window.location.pathname.replace(/\/+$/, '') === '/entrar' || window.location.hash === '#login')
    window.history.replaceState(null, '', '/' + window.location.search);
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [stage, setStage] = useState<Stage>('login');
  const [account, setAccount] = useState<Account | null>(null);
  const [shard, setShard] = useState<Shard | null>(null);
  const [character, setCharacter] = useState<CharacterSummary | null>(null);
  const [busy, setBusy] = useState(false);
  const [verificando, setVerificando] = useState(useHttp);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [confirmacao, setConfirmacao] = useState<string | null>(null);
  const [doisFatores, setDoisFatores] = useState<string | null>(null);
  const logado = useRef(false);

  useEffect(() => aoGravarIdioma(id => {
    if (!logado.current) return;
    void api.gravarIdioma(id).catch(() => {});
  }), []);

  useEffect(() => {
    if (!useHttp) return;
    let live = true;
    api.me()
      .then(next => {
        if (!live) return;
        logado.current = true;
        setAccount(next);
        alinharIdioma(next.idioma, true);
        setStage('shard');
        sairDaRotaDeEntrada();
      })
      .catch(err => {
        if (!live) return;
        const aviso = erroDeBoot(err);
        if (aviso) setError(aviso);
      })
      .finally(() => { if (live) setVerificando(false); });
    return () => { live = false; };
  }, []);

  // O mundo caiu porque a conta perdeu o acesso à fase (aviso 147): volta à entrada com o motivo.
  useEffect(() => aoEncerrarAcesso(() => {
    logado.current = false;
    setShardHeader(null);
    setAccount(null);
    setShard(null);
    setCharacter(null);
    setError(t('acesso.semAcesso'));
    setErrorCode('acesso.semAcesso');
    setStage('login');
    window.history.replaceState(null, '', '/#login');
  }), []);

  const enterAfterAuth = useCallback(async (run: () => Promise<Account>, email?: string) => {
    setBusy(true);
    setError(null);
    setErrorCode(null);
    try {
      const conta = await run();
      logado.current = true;
      setConfirmacao(null);
      setDoisFatores(null);
      setAccount(conta);
      alinharIdioma(conta.idioma, true);
      setStage('shard');
      sairDaRotaDeEntrada();
    } catch (err) {
      // Conta criada (ou senha certa) com o e-mail ainda sem confirmar: não é erro, é o próximo passo.
      if (err instanceof ApiError && err.code === CONFIRMAR_EMAIL && email) {
        setConfirmacao(email);
        return;
      }
      // Conta da equipe: a senha conferiu, falta o código do app.
      if (err instanceof DesafioDoisFatores) {
        setDoisFatores(err.desafio);
        return;
      }
      // O desafio venceu (5 min ou 5 tentativas): volta para a senha, com o aviso.
      if (err instanceof ApiError && err.code === DOIS_FATORES_DESAFIO) setDoisFatores(null);
      setError(readable(err));
      setErrorCode(err instanceof ApiError ? err.code ?? null : null);
    } finally {
      setBusy(false);
    }
  }, []);

  const signIn = useCallback(
    (credentials: Credentials) => enterAfterAuth(() => api.signIn(credentials), credentials.email.trim()),
    [enterAfterAuth]
  );

  const signUp = useCallback(
    (credentials: Credentials) => enterAfterAuth(() => api.signUp(credentials), credentials.email.trim()),
    [enterAfterAuth]
  );

  const confirmarEmail = useCallback(async (codigo: string, serial?: string) => {
    if (!confirmacao) return;
    const email = confirmacao;
    await enterAfterAuth(() => api.confirmarEmail(serial ? { email, codigo, serial } : { email, codigo }));
  }, [confirmacao, enterAfterAuth]);

  const reenviarCodigo = useCallback(async () => {
    if (!confirmacao) return;
    setError(null);
    setErrorCode(null);
    try {
      await api.reenviarCodigo(confirmacao);
    } catch (err) {
      setError(readable(err));
      setErrorCode(err instanceof ApiError ? err.code ?? null : null);
      throw err;
    }
  }, [confirmacao]);

  const iniciarConfirmacao = useCallback((email: string) => {
    setError(null);
    setErrorCode(null);
    setConfirmacao(email.trim());
  }, []);

  const cancelarConfirmacao = useCallback(() => {
    setError(null);
    setErrorCode(null);
    setConfirmacao(null);
  }, []);

  const confirmarDoisFatores = useCallback(async (codigo: { codigo?: string; recuperacao?: string }) => {
    if (!doisFatores) return;
    const desafio = doisFatores;
    await enterAfterAuth(() => api.signIn2fa({ desafio, ...codigo }));
  }, [doisFatores, enterAfterAuth]);

  const cancelarDoisFatores = useCallback(() => {
    setError(null);
    setErrorCode(null);
    setDoisFatores(null);
  }, []);

  const redefinirSenha = useCallback(async (email: string, codigo: string, senha: string, serial?: string) => {
    const corpo = { email: email.trim(), codigo, senha };
    await enterAfterAuth(() => api.redefinirSenha(serial ? { ...corpo, serial } : corpo));
  }, [enterAfterAuth]);

  const signInGoogle = useCallback(
    (body: CredencialGoogle) => enterAfterAuth(() => api.signInGoogle(body)),
    [enterAfterAuth]
  );

  const signOut = useCallback(async () => {
    try {
      await api.signOut();
    } finally {
      logado.current = false;
      setShardHeader(null);
      setAccount(null);
      setShard(null);
      setCharacter(null);
      setError(null);
      setErrorCode(null);
      setStage('login');
    }
  }, []);

  const chooseShard = useCallback((next: Shard) => {
    setShardHeader(next.id);
    setShard(next);
    setCharacter(null);
    setStage('character');
  }, []);

  const enterWorld = useCallback((next: CharacterSummary) => {
    setShardHeader(next.shardId);
    setCharacter(next);
    setStage('world');
  }, []);

  const marcarTutorialFeito = useCallback(() => {
    setCharacter(atual => atual ? { ...atual, tutorial: false } : atual);
  }, []);

  const startCreate = useCallback(() => setStage('create'), []);
  const cancelCreate = useCallback(() => setStage('character'), []);
  const finishCreate = useCallback(() => setStage('character'), []);

  const back = useCallback(() => {
    setError(null);
    setStage(current => {
      if (current === 'world') return 'character';
      if (current === 'create') return 'character';
      if (current === 'character') { setShard(null); return 'shard'; }
      return 'login';
    });
  }, []);

  const clearError = useCallback(() => { setError(null); setErrorCode(null); }, []);

  const value = useMemo<SessionValue>(() => ({
    stage, account, shard, character, busy, verificando, error, errorCode, confirmacao, doisFatores,
    signIn, signUp, signInGoogle, confirmarEmail, reenviarCodigo, iniciarConfirmacao, cancelarConfirmacao, redefinirSenha,
    confirmarDoisFatores, cancelarDoisFatores,
    signOut, chooseShard, enterWorld, marcarTutorialFeito, startCreate, cancelCreate, finishCreate, back, clearError
  }), [stage, account, shard, character, busy, verificando, error, errorCode, confirmacao, doisFatores,
       signIn, signUp, signInGoogle, confirmarEmail, reenviarCodigo, iniciarConfirmacao, cancelarConfirmacao, redefinirSenha,
       confirmarDoisFatores, cancelarDoisFatores,
       signOut, chooseShard, enterWorld, marcarTutorialFeito, startCreate, cancelCreate, finishCreate, back, clearError]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession precisa estar dentro de <SessionProvider>.');
  return value;
}
